<template>
  <div>
    <Loader :loading="showLoader" />
    
    <transition name="map-fade">
      <div v-if="showMap" class="map-overlay" @click.self="closeMap">
        <Loader :loading="loadingBookings" />
        
        <div class="map-wrapper" :class="{ 'map-fullscreen': isFullScreen }">
          <div class="map-header">
            <div class="header-left">
              <div class="header-icon-box">
                <v-icon color="white" size="28">mdi-map-marker-radius</v-icon>
              </div>
              <div class="header-info">
                <h2 class="header-title">{{ $t('map.interactive-map') }}</h2>
                <p class="header-subtitle">{{ selectedFloor.name || 'Building Overview' }}</p>
              </div>
            </div>
            
            <div class="header-actions">
              <button class="header-action-btn" @click="toggleFullScreen">
                <v-icon>{{ isFullScreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
              </button>
              <button class="header-action-btn" @click="closeMap">
                <v-icon>mdi-close</v-icon>
              </button>
            </div>
          </div>

          <div class="floor-selector-wrapper" v-if="floors.length > 0">
            <button 
              class="floor-selector-toggle" 
              @click="showFloorMenu = !showFloorMenu"
            >
              <v-icon size="20">mdi-layers-triple</v-icon>
              <span>{{ selectedFloor.name }}</span>
              <v-icon size="18">{{ showFloorMenu ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </button>
            
            <transition name="floor-menu-slide">
              <div v-if="showFloorMenu" class="floor-menu">
                <button
                  v-for="(floor, index) in lodash.orderBy(floors, ['number'], ['asc'])"
                  :key="index"
                  class="floor-menu-item"
                  :class="{ 'active': floor.id === selectedFloor.id }"
                  @click="changeFloor(floor)"
                >
                  <div class="floor-item-icon">
                    <v-icon size="18" :color="floor.id === selectedFloor.id ? 'white' : '#718096'">
                      mdi-floor-plan
                    </v-icon>
                  </div>
                  <span class="floor-item-name">{{ floor.name }}</span>
                  <v-icon v-if="floor.id === selectedFloor.id" size="18" color="#28A745">
                    mdi-check-circle
                  </v-icon>
                </button>
              </div>
            </transition>
          </div>

          <div class="availability-btn-wrapper">
            <button 
              class="map-control-btn availability-btn" 
              :class="{ 'active': showAvailability }"
              @click="openRangeDatePicker"
              title="Check Availability"
            >
              <v-icon>mdi-calendar-clock</v-icon>
            </button>
          </div>

          <div class="map-legend" v-if="showAvailability">
            <div class="legend-header">
              <v-icon size="16" color="#285096">mdi-information-outline</v-icon>
              <span>Legend</span>
            </div>
            <div class="legend-items">
              <div class="legend-item">
                <div class="legend-dot available"></div>
                <span>Available</span>
              </div>
              <div class="legend-item">
                <div class="legend-dot occupied"></div>
                <span>Occupied</span>
              </div>
            </div>
          </div>

          <div class="map-container-wrapper">
            <l-map
              ref="map"
              v-if="showMap && selectedFloor?.floorPlan && selectedFloor?.bounds"
              @load="adjustMapSize"
              @ready="onMapReady"
              :style="{ width: '100%', height: '100%' }"
              :noBlockingAnimations="true"
              :maxZoom="22"
              :minZoom="16"
              :zoom="zoom"
              :center="floorCenter"
              :bounds="selectedFloor?.bounds"
              :zoomControl="false"
              class="leaflet-map"
            >
              <l-tile-layer 
                :url="url" 
                :attribution="attribution"
                :opacity="1"
              />
              <l-image-overlay 
                :url="selectedFloor?.floorPlan" 
                :bounds="selectedFloor?.bounds"
                :opacity="0.5"
                :interactive="false"
              />

              <l-marker
                v-for="marker in lodash.uniqBy(markers, 'poiId')"
                :key="marker.poiId"
                :lat-lng="marker.latlong"
                :icon="getMarkerIcon(marker)"
              >
                <l-tooltip 
                  :permanent="true" 
                  direction="top"
                  class="marker-tooltip"
                  :offset="[0, -10]"
                >
                  <div class="marker-tooltip-content">
                    <v-icon size="12" color="#285096">mdi-map-marker</v-icon>
                    <span class="marker-name">{{ marker.poi.name }}</span>
                    <v-icon size="12" color="#23A5E6">mdi-account-group</v-icon>
                    <span class="marker-capacity">{{ marker.poi.capacity }}</span>
                  </div>
                </l-tooltip>
                <l-popup class="marker-popup" :offset="[0, -15]">
                  <div class="popup-header">
                    <v-icon color="#285096" size="18">mdi-map-marker</v-icon>
                    <span class="popup-title">{{ marker?.poi?.name }}</span>
                  </div>
                  <div class="popup-content">
                    <div class="popup-row">
                      <v-icon size="14" color="#23A5E6">mdi-tag-outline</v-icon>
                      <span>{{ marker?.category?.name }}</span>
                    </div>
                    <div class="popup-row">
                      <v-icon size="14" color="#23A5E6">mdi-account-group</v-icon>
                      <span>{{ marker?.poi?.capacity }} persons</span>
                    </div>
                    <div class="popup-row">
                      <v-icon size="14" color="#23A5E6">mdi-clock-outline</v-icon>
                      <span>{{ marker?.poi?.availableFrom || '06:00' }} - {{ marker?.poi?.availableUntil || '20:00' }}</span>
                    </div>
                  </div>
                  <button 
                    v-if="$route.name !== 'poiDetails'" 
                    class="popup-btn"
                    @click="goToPoiDetails(marker?.poi)"
                  >
                    <v-icon size="14">mdi-information-outline</v-icon>
                    <span>View Details</span>
                  </button>
                </l-popup>
              </l-marker>

              <l-polygon
                v-for="(polygon, index) in lodash.uniqBy(polygons, 'poiId')"
                :key="index"
                :lat-lngs="polygon.latLng"
                :color="polygon.color"
                :fill-color="polygon.fillColor"
                :fill-opacity="polygon.fillOpacity"
                :weight="polygon.weight"
                class="custom-polygon"
              >
              </l-polygon>
            </l-map>
          </div>
        </div>
      </div>
    </transition>

    <transition name="dialog-fade">
      <div v-if="availabilityDialog" class="custom-dialog-overlay" @click.self="discardRange">
        <div class="custom-dialog availability-dialog">
          <div class="dialog-header">
            <div class="dialog-header-content">
              <div class="dialog-icon">
                <v-icon color="white" size="24">mdi-calendar-clock</v-icon>
              </div>
              <div class="dialog-title-wrap">
                <h3 class="dialog-title">Check Availability</h3>
                <p class="dialog-subtitle">Select date and time range</p>
              </div>
            </div>
            <button class="dialog-close-btn" @click="discardRange">
              <v-icon color="white">mdi-close</v-icon>
            </button>
          </div>
          <div class="dialog-body">
            <DatePicker
              :validHours="validHours"
              :max-date="maxDate"
              ref="rangeDatePicker"
              color="#285096"
              is-dark
              :is24hr="true"
              :min-date="new Date()"
              :minute-increment="15"
              is-range
              v-model="range"
              mode="dateTime"
              class="date-picker"
            />
          </div>
          <div class="dialog-actions">
            <button class="dialog-btn dialog-btn-cancel" @click="discardRange">
              <v-icon size="18">mdi-close</v-icon>
              <span>Cancel</span>
            </button>
            <button class="dialog-btn dialog-btn-confirm" @click="confirmRange">
              <v-icon size="18">mdi-check</v-icon>
              <span>Confirm</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import { LMap, LTileLayer, LMarker, LImageOverlay, LPopup, LPolygon, LTooltip } from '@vue-leaflet/vue-leaflet'
import { ref, defineComponent, nextTick, computed } from 'vue'
import { getMarkerIcon } from '@/services/markerIcons'
import lodash from 'lodash'
import { DatePicker } from 'v-calendar'
import Loader from '@/components/general/Loader'
import { getFile, getDataFromBlob, getFloors, getReservationsByPoi } from '@/controllers/BaseController'
import { latLngBounds, latLng } from 'leaflet'
import { useRoute, useRouter } from 'vue-router'

export default defineComponent({
  name: 'MapView',
  components: {
    LMap,
    LTooltip,
    Loader,
    DatePicker,
    LTileLayer,
    LMarker,
    LImageOverlay,
    LPopup,
    LPolygon,
  },
  setup() {
    const showMap = ref(false)
    const zoom = ref(19)
    const availabilityDialog = ref(false)
    const showAvailability = ref(false)
    const showFloorMenu = ref(false)
    const validHours = ref({ min: 6, max: 20 })
    const loadingBookings = ref(false)
    const markers = ref([])
    const polygons = ref([])
    const url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    const attribution = '© OpenStreetMap'
    const selectedFloor = ref({})
    const floorCenter = ref({})
    const isFullScreen = ref(false)
    const map = ref(null)
    const showLoader = ref(false)
    const floors = ref([])
    const selectedBuilding = ref({})
    const categories = ref([])
    const pois = ref([])
    const route = useRoute()
    const router = useRouter()

    const getInitialDateRange = () => {
      const startDate = new Date()
      const endDate = new Date(startDate)
      endDate.setMinutes(startDate.getMinutes() + 10)

      if (endDate.getHours() >= 20 && endDate.getMinutes() > 0) {
        startDate.setDate(startDate.getDate() + 1)
        startDate.setHours(6, 0, 0, 0)
        endDate.setDate(endDate.getDate() + 1)
        endDate.setHours(6, 10, 0, 0)
      }

      return { start: startDate, end: endDate }
    }
    const range = ref(getInitialDateRange())
    const tempRange = ref(getInitialDateRange())

    const maxDate = computed(() => {
      const today = new Date()
      return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 57)
    })

    const closeMap = () => {
      showMap.value = false
      showFloorMenu.value = false
    }

    const toggleFullScreen = () => {
      isFullScreen.value = !isFullScreen.value
      nextTick(() => {
        setTimeout(() => {
          if (map.value && map.value.mapObject) {
            map.value.mapObject.invalidateSize()
          }
        }, 100)
      })
    }

    const openRangeDatePicker = () => {
      availabilityDialog.value = true
      tempRange.value = range.value
    }

    const discardRange = () => {
      availabilityDialog.value = false
      range.value = tempRange.value
    }

    const confirmRange = async () => {
      availabilityDialog.value = false
      tempRange.value = range.value
      await handleAvailability()
    }

    const handleAvailability = async () => {
      loadingBookings.value = true
      const startDateTime = range.value.start
      const endDateTime = range.value.end

      const updatedPolygons = await Promise.all(
        polygons.value.map(async polygon => {
          const poi = polygon.poi
          const reservations = await getReservationsByPoi(poi?.id, startDateTime, endDateTime)
          if (reservations.length === 0) {
            polygon.fillColor = '#71aca2'
            polygon.color = '#28A745'
            polygon.fillOpacity = 0.5
            polygon.weight = 3
          } else {
            polygon.fillColor = '#DC3545'
            polygon.color = '#DC3545'
            polygon.fillOpacity = 0.5
            polygon.weight = 3
          }
          return polygon
        })
      )
      polygons.value = JSON.parse(JSON.stringify(updatedPolygons))
      setTimeout(() => {
        showAvailability.value = true
        loadingBookings.value = false
      }, 800)
    }

    async function openMap(selectedBuildingParam, poisParam, categoriesParam) {
      showLoader.value = true
      floors.value = []
      selectedBuilding.value = selectedBuildingParam
      pois.value = lodash.filter(poisParam, item => item.buildingId === selectedBuildingParam.id)
      categories.value = categoriesParam

      try {
        let fetchedFloors = await getFloors()
        fetchedFloors = lodash.filter(fetchedFloors, floor => {
          if (!floor.building || !floor.northEastCorner || !floor.southWestCorner) {
            return false
          }
          return floor.building.id === selectedBuildingParam.id && floor.active
        })

        if (categories.value?.length === 1) {
          const categoryPoiIds = new Set(categories.value[0].pois.ids)
          fetchedFloors = fetchedFloors.filter(floor => floor.pois.ids.some(poiId => categoryPoiIds.has(poiId)))
        }

        fetchedFloors = fetchedFloors.sort((a, b) => a.number - b.number)

        const floorIds = new Set(pois.value.map(poi => poi?.floors?.ids[0]))
        fetchedFloors = fetchedFloors.filter(floor => floorIds.has(floor.id))

        if (route.name === 'poiDetails') {
          fetchedFloors = [pois.value[0]?.floor]
          floors.value = fetchedFloors
        }

        const floorPromises = fetchedFloors.map(async (floor, index) => {
          let floorPlan = floor.configs?.floorplan
          if (!floor.northEastCorner || !floor.southWestCorner) {
            return
          }
          floor['bounds'] = latLngBounds([
            [floor.northEastCorner.lat, floor.northEastCorner.long],
            [floor.southWestCorner.lat, floor.southWestCorner.long],
          ])
          floor['center'] = latLng(floor.northEastCorner.lat, floor.northEastCorner.long)
          if (floorPlan) {
            floorPlan = await getFile(floorPlan)
            floorPlan = await getDataFromBlob(floorPlan)
            floor['floorPlan'] = floorPlan
            floors.value.push(floor)
          } else {
            floor['floorPlan'] = null
            floors.value.push(floor)
          }
          if (index === 0) {
            selectedFloor.value = floor
            getMarkers()
          }
        })

        await Promise.all(floorPromises)
      } catch (error) {
        console.error('Error in openMap:', error)
      } finally {
        showLoader.value = false
        showMap.value = true
      }
    }

    const changeFloor = floor => {
      selectedFloor.value = floor
      showFloorMenu.value = false
      getMarkers()
      
      // Update floor plan opacity after floor change
      nextTick(() => {
        setTimeout(() => {
          if (map.value && map.value.mapObject) {
            const zoom = map.value.mapObject.getZoom()
            const imageOverlay = document.querySelector('.leaflet-image-layer')
            
            if (imageOverlay) {
              if (zoom < 18) {
                imageOverlay.style.opacity = '0.3'
              } else if (zoom >= 18 && zoom < 20) {
                imageOverlay.style.opacity = '0.5'
              } else if (zoom >= 20 && zoom < 21) {
                imageOverlay.style.opacity = '0.65'
              } else {
                imageOverlay.style.opacity = '0.75'
              }
            }
          }
        }, 300)
      })
    }

    function adjustMapSize() {
      nextTick(() => {
        setTimeout(() => {
          if (map.value && map.value.mapObject) {
            map.value.mapObject.invalidateSize()
          }
        }, 100)
      })
    }

    function onMapReady() {
      adjustMapSize()
      
      // Add zoom-based opacity control for floor plan
      if (map.value && map.value.mapObject) {
        const updateFloorPlanOpacity = () => {
          const zoom = map.value.mapObject.getZoom()
          const imageOverlay = document.querySelector('.leaflet-image-layer')
          
          if (imageOverlay) {
            if (zoom < 18) {
              // Low zoom: show streets clearly, floor plan faint
              imageOverlay.style.opacity = '0.3'
            } else if (zoom >= 18 && zoom < 20) {
              // Medium zoom: balanced
              imageOverlay.style.opacity = '0.5'
            } else if (zoom >= 20 && zoom < 21) {
              // High zoom: floor plan more visible
              imageOverlay.style.opacity = '0.65'
            } else {
              // Very high zoom: floor plan very visible, streets faint
              imageOverlay.style.opacity = '0.75'
            }
          }
        }
        
        // Update on zoom
        map.value.mapObject.on('zoomend', updateFloorPlanOpacity)
        
        // Initial update
        setTimeout(updateFloorPlanOpacity, 300)
      }
    }

    function getMarkers() {
      let poisData = pois.value
      let categoriesData = categories.value
      markers.value = []
      polygons.value = []
      poisData.forEach(poi => {
        if (!poi.locations || !poi.locations[0]) {
          return
        }
        let category = lodash.find(categoriesData, category => category?.id === poi?.poiCategories?.ids[0])
        let marker = {
          poiId: poi?.id,
          poi: poi,
          category: category,
          latlong: [poi.locations[0].center.lat, poi.locations[0].center.long],
        }
        if (marker && poi?.floors?.ids[0] === selectedFloor.value?.id) {
          markers.value.push(marker)
        }

        poi.locations.forEach(location => {
          if (!location.polygon) {
            return
          }
          let polygon = location.polygon
          let poiPolygon = {
            poi: poi,
            latLng: undefined,
            color: '#3EB39F',
            poiId: poi?.id,
            weight: 2,
            fillColor: '#71aca2',
            fillOpacity: 0.4,
          }

          poiPolygon.latLng = polygon
            .map(point => {
              if (!point.lat || !point.long) {
                return null
              }
              return [point.lat, point.long]
            })
            .filter(Boolean)

          if (poiPolygon && poi?.floors?.ids[0] === selectedFloor.value?.id) {
            const latLngExists = polygons.value.some(
              existingPolygon => JSON.stringify(existingPolygon.latLng) === JSON.stringify(poiPolygon.latLng)
            )

            if (!latLngExists) {
              polygons.value.push(poiPolygon)
            }
          }
        })
      })
      if (markers.value.length === 1) {
        floorCenter.value = markers.value[0].latlong
      } else {
        floorCenter.value = selectedFloor.value.bounds.getCenter()
      }

      polygons.value = lodash.uniq(polygons.value, 'poiId')
    }

    function goToPoiDetails(poi) {
      router
        .push({
          name: 'PoiDetails',
          params: { building_id: poi.building.id, poi_id: poi?.id, imagePath: poi?.image },
        })
        .catch(() => {})
    }

    return {
      showMap,
      zoom,
      markers,
      polygons,
      url,
      attribution,
      selectedFloor,
      map,
      showLoader,
      floors,
      selectedBuilding,
      categories,
      pois,
      openMap,
      adjustMapSize,
      onMapReady,
      getMarkers,
      goToPoiDetails,
      lodash,
      changeFloor,
      getMarkerIcon,
      floorCenter,
      showAvailability,
      availabilityDialog,
      range,
      validHours,
      maxDate,
      openRangeDatePicker,
      discardRange,
      confirmRange,
      handleAvailability,
      loadingBookings,
      isFullScreen,
      toggleFullScreen,
      closeMap,
      showFloorMenu,
    }
  },
})
</script>

<style scoped lang="scss">
@import '~leaflet/dist/leaflet.css';

.map-fade-enter-active,
.map-fade-leave-active {
  transition: opacity 0.3s ease;
}

.map-fade-enter-from,
.map-fade-leave-to {
  opacity: 0;
}

.map-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.map-wrapper {
  width: 95%;
  height: 90vh;
  max-width: 1600px;
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  position: relative;
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    transform: translateY(50px) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

.map-fullscreen {
  width: 100%;
  height: 100vh;
  max-width: none;
  border-radius: 0;
}

.map-header {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 3px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.header-icon-box {
  width: 52px;
  height: 52px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-title {
  font-size: 1.375rem;
  font-weight: 800;
  color: white;
  margin: 0;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.header-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.header-action-btn {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.header-action-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.floor-selector-wrapper {
  position: absolute;
  top: 100px;
  right: 24px;
  z-index: 1000;
}

.floor-selector-toggle {
  background: white;
  border: 2px solid rgba(40, 80, 150, 0.15);
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #285096;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(40, 80, 150, 0.2);
  min-width: 180px;
  justify-content: space-between;
}

.floor-selector-toggle:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.3);
  border-color: #285096;
}

.availability-btn-wrapper {
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

.floor-menu-slide-enter-active,
.floor-menu-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top;
}

.floor-menu-slide-enter-from,
.floor-menu-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px) scaleY(0.9);
}

.floor-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 2px solid rgba(40, 80, 150, 0.15);
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(40, 80, 150, 0.25);
  min-width: 200px;
  max-height: 350px;
  overflow-y: auto;
  padding: 8px;
}

.floor-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4E5965;
}

.floor-menu-item:hover {
  background: rgba(40, 80, 150, 0.08);
  transform: translateX(4px);
}

.floor-menu-item.active {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);
  color: white;
}

.floor-item-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(40, 80, 150, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.floor-menu-item.active .floor-item-icon {
  background: rgba(255, 255, 255, 0.2);
}

.floor-item-name {
  flex: 1;
}

.map-control-btn {
  width: 56px;
  height: 56px;
  background: white;
  border: 2px solid rgba(40, 80, 150, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #285096;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(40, 80, 150, 0.2);
}

.map-control-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.3);
  border-color: #285096;
}

.availability-btn.active {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);
  color: white;
  border-color: white;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}

.map-legend {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: white;
  border: 2px solid rgba(40, 80, 150, 0.15);
  border-radius: 14px;
  padding: 14px 20px;
  box-shadow: 0 4px 20px rgba(40, 80, 150, 0.2);
  display: flex;
  align-items: center;
  gap: 20px;
}

.legend-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #285096;
  font-size: 0.875rem;
  padding-right: 20px;
  border-right: 2px solid rgba(40, 80, 150, 0.15);
}

.legend-items {
  display: flex;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #4E5965;
}

.legend-dot {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid rgba(0, 0, 0, 0.15);
}

.legend-dot.available {
  background: #71aca2;
}

.legend-dot.occupied {
  background: #DC3545;
}

.map-container-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.leaflet-map {
  width: 100%;
  height: 100%;
}

.leaflet-map :deep(.leaflet-control-attribution) {
  display: none !important;
}

.leaflet-map :deep(.leaflet-tile-pane) {
  opacity: 1 !important;
}

:deep(.marker-tooltip) {
  background: white !important;
  border: 2px solid #285096 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 16px rgba(40, 80, 150, 0.3) !important;
  padding: 8px 12px !important;
}

:deep(.marker-tooltip::before) {
  border-top-color: #285096 !important;
}

.marker-tooltip-content {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #001437;
  font-size: 0.8125rem;
  white-space: nowrap;
}

.marker-name {
  color: #001437;
}

.marker-capacity {
  color: #23A5E6;
}

:deep(.marker-popup .leaflet-popup-content-wrapper) {
  background: white;
  padding: 0;
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.25);
  border-radius: 12px;
  border: 2px solid rgba(40, 80, 150, 0.15);
  overflow: hidden;
}

:deep(.marker-popup .leaflet-popup-content) {
  margin: 0;
  min-width: 180px;
}

:deep(.marker-popup .leaflet-popup-tip) {
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(40, 80, 150, 0.15);
  border-top: none;
  border-right: none;
}

:deep(.marker-popup .leaflet-popup-close-button) {
  color: #718096 !important;
  font-size: 18px !important;
  padding: 8px 12px !important;
  font-weight: 700 !important;
}

:deep(.marker-popup .leaflet-popup-close-button:hover) {
  color: #285096 !important;
}

.popup-header {
  background: linear-gradient(135deg, rgba(40, 80, 150, 0.1) 0%, rgba(35, 165, 230, 0.1) 100%);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 2px solid rgba(40, 80, 150, 0.15);
}

.popup-title {
  font-size: 0.875rem;
  font-weight: 800;
  color: #001437;
  line-height: 1.2;
}

.popup-content {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: white;
}

.popup-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4E5965;
}

.popup-btn {
  width: 100%;
  background: #285096;
  color: white;
  border: none;
  border-radius: 0;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  border-top: 2px solid rgba(40, 80, 150, 0.15);
}

.popup-btn:hover {
  background: #23A5E6;
}

:deep(.custom-polygon) {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

:deep(.custom-polygon:hover) {
  stroke-width: 4;
  fill-opacity: 0.7 !important;
}

.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.3s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

.custom-dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.custom-dialog {
  background: white;
  border-radius: 20px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 450px;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.dialog-header {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.dialog-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.dialog-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.dialog-subtitle {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-weight: 500;
}

.dialog-close-btn {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
}

.dialog-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.dialog-body {
  padding: 24px;
}

.dialog-actions {
  padding: 20px 24px;
  background: #F8FAFC;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-top: 2px solid rgba(40, 80, 150, 0.1);
}

.dialog-btn {
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialog-btn-cancel {
  background: white;
  color: #718096;
  border: 2px solid #E2E8F0;
}

.dialog-btn-cancel:hover {
  background: #F8FAFC;
  border-color: #CBD5E0;
}

.dialog-btn-confirm {
  background: linear-gradient(135deg, #28A745 0%, #20C997 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(40, 167, 69, 0.3);
}

.dialog-btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

@media (max-width: 960px) {
  .map-wrapper {
    width: 100%;
    height: 95vh;
    border-radius: 20px;
  }

  .map-header {
    padding: 16px 20px;
  }

  .header-title {
    font-size: 1.125rem;
  }

  .header-subtitle {
    font-size: 0.8125rem;
  }

  .floor-selector-wrapper {
    top: 90px;
    right: 16px;
  }

  .floor-selector-toggle {
    padding: 12px 16px;
    font-size: 0.875rem;
    min-width: 150px;
  }

  .availability-btn-wrapper {
    bottom: 16px;
    right: 16px;
  }

  .map-legend {
    bottom: 16px;
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
  }

  .legend-header {
    padding-right: 0;
    padding-bottom: 12px;
    border-right: none;
    border-bottom: 2px solid rgba(40, 80, 150, 0.15);
  }
}

@media (max-width: 600px) {
  .map-overlay {
    padding: 0;
  }

  .map-wrapper {
    width: 100%;
    height: 100vh;
    border-radius: 0;
  }

  .map-header {
    padding: 12px 16px;
  }

  .header-icon-box {
    width: 44px;
    height: 44px;
  }

  .header-title {
    font-size: 1rem;
  }

  .header-subtitle {
    font-size: 0.75rem;
  }

  .header-action-btn {
    width: 40px;
    height: 40px;
  }

  .floor-selector-wrapper {
    top: 75px;
    right: 12px;
  }

  .floor-selector-toggle {
    padding: 10px 14px;
    font-size: 0.8125rem;
    min-width: 130px;
  }

  .availability-btn-wrapper {
    bottom: 12px;
    right: 12px;
  }

  .map-control-btn {
    width: 48px;
    height: 48px;
  }

  .map-legend {
    bottom: 12px;
    padding: 10px 14px;
    font-size: 0.75rem;
  }

  .legend-items {
    gap: 12px;
  }

  .legend-item {
    font-size: 0.75rem;
  }

  .legend-dot {
    width: 16px;
    height: 16px;
  }

  .custom-dialog {
    max-width: 100%;
    border-radius: 16px;
  }

  .dialog-header {
    padding: 20px;
  }

  .dialog-icon {
    width: 44px;
    height: 44px;
  }

  .dialog-title {
    font-size: 1.125rem;
  }

  .dialog-subtitle {
    font-size: 0.75rem;
  }

  .dialog-body {
    padding: 20px;
  }

  .dialog-actions {
    padding: 16px 20px;
    flex-direction: column;
  }

  .dialog-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>